export interface IPooledResult<T, R> {
    item: T;
    index: number;
    status: "fulfilled" | "rejected";
    value?: R;
    error?: any;
}

export interface IConcurrencyPoolOptions {
    concurrency?: number;
    // Cooperative cancellation: checked before each item starts. Items already in
    // flight complete normally; their results are still returned.
    isCancelled?: () => boolean;
}

const DEFAULT_CONCURRENCY = 4;

/**
 * Runs `worker` over `items` with a bounded number of concurrent executions.
 * Never rejects: each item resolves to a fulfilled/rejected entry (allSettled
 * semantics) so one failing item cannot sink the whole fan-out. Results are
 * returned in input order regardless of completion order.
 */
export const runWithConcurrency = async <T, R>(
    items: T[],
    worker: (item: T, index: number) => Promise<R>,
    options?: IConcurrencyPoolOptions
): Promise<IPooledResult<T, R>[]> => {
    const results: IPooledResult<T, R>[] = new Array(items?.length ?? 0);
    if (!Array.isArray(items) || items.length === 0) {
        return [];
    }

    const concurrency = Math.max(1, Math.min(options?.concurrency ?? DEFAULT_CONCURRENCY, items.length));
    let nextIndex = 0;

    const runWorker = async (): Promise<void> => {
        while (nextIndex < items.length) {
            if (options?.isCancelled?.()) {
                while (nextIndex < items.length) {
                    const skippedIndex = nextIndex++;
                    results[skippedIndex] = { item: items[skippedIndex], index: skippedIndex, status: "rejected", error: new Error("cancelled") };
                }
                return;
            }

            const currentIndex = nextIndex++;
            try {
                const value = await worker(items[currentIndex], currentIndex);
                results[currentIndex] = { item: items[currentIndex], index: currentIndex, status: "fulfilled", value };
            } catch (error) {
                results[currentIndex] = { item: items[currentIndex], index: currentIndex, status: "rejected", error };
            }
        }
    };

    await Promise.all(Array.from({ length: concurrency }, () => runWorker()));

    return results;
};
